import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"
import { fetchCollaborations } from "slices/campaigns/collaborations/thunk";
import { AppDispatch, RootState } from "store";
import PostPerformanceOffcanvas from "../collaborations/PostPerformanceOffcanvas";
import InfiniteScroll from "react-infinite-scroll-component";
import { formatNumber, generateUniqueKey } from "utils/helpers";
import { SortOptionLabel, SortOptions } from "types/campaign/collaborations";
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import EyeIcon from "assets/icons/EyeIcon";
import TargetIcon from "assets/icons/TargetIcon";
import RupeeIcon2 from "assets/icons/RupeeIcon2";
import ContentIcon from "assets/icons/ContentIcon";
import { updateCollaborationTableFilters } from "slices/campaigns/collaborations/reducer";
import userProfilePicture from "../../../assets/images/users/user-dummy-img.jpg";
import WebIcon1 from "assets/icons/WebIcon1";

const CollaborationsTable = () => {

    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const [selectedCollaboration, setSelectedCollaboration] = useState<string | null>(null);

    const { tableFilters: filters } = useSelector((state: RootState) => state.collaborations);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign);

    const collaborationsData = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.filtered_lists?.[filterId!]);
    const collaborations = collaborationsData?.list ?? [];
    const page = collaborationsData?.page ?? 1;
    const has_more = collaborationsData?.has_more ?? true;

    const loading = useSelector((state: RootState) => state.collaborations.loading);
    const error = useSelector((state: RootState) => state.collaborations.error);

    const [initialLoading, setInitialLoading] = useState(true);
    const connectionRequired = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign?.connection_required);


    useEffect(() => {
        if (campaignId) {
            setInitialLoading(true);
            dispatch(fetchCollaborations({ filters, campaignId }))
                .finally(() => {
                    setFilterId(generateUniqueKey(filters, ['page']));
                    setInitialLoading(false);
                })
        }
    }, [campaignId, filters])

    const nextPage = () => {
        if (has_more && campaignId) {
            const nextPageNumber = page + 1;
            dispatch(fetchCollaborations({ filters: { ...filters, page: nextPageNumber }, campaignId }))
        }
    }

    const updateSorting = (sortOption: SortOptions) => {
        let updated_filters = { ...filters, sort: sortOption };
        delete updated_filters.order;
        if ([SortOptions.LOWEST_CPE, SortOptions.LOWEST_CPV].includes(sortOption)) {
            updated_filters.order = 'asc';
        }
        dispatch(updateCollaborationTableFilters(updated_filters));
    }

    return (
        <React.Fragment>
            <div className="card campaign-insights-card">
                <div className="card-header d-flex justify-content-between align-items p-4 border-0 flex-column flex-lg-row gap-2 gap-lg-0">
                    <h1>Influencer Insights</h1>
                    <UncontrolledDropdown className='sort-dropdown me-2'>
                        <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                            <span>
                                <span className='text-muted me-1'>Sort by: </span>
                                {SortOptionLabel[filters.sort]}
                            </span>
                            <i className='mdi mdi-chevron-down' />
                        </DropdownToggle>
                        <DropdownMenu className='mt-1 p-0 w-100'>
                            {
                                Object.values(SortOptions).map((sortOption, i) => <DropdownItem key={i} onClick={() => updateSorting(sortOption)}>
                                    {SortOptionLabel[sortOption]}
                                </DropdownItem>
                                )
                            }
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </div>
                <div className="card-body p-0 m-0">
                    <InfiniteScroll
                        dataLength={collaborations.length}
                        next={nextPage}
                        hasMore={has_more}
                        loader={<></>}
                        className="w-100"
                    >
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Views</th>
                                    <th>Interactions</th>
                                    <th>CPV</th>
                                    <th>CPE</th>
                                    {
                                        campaign?.mode !== 'offline' && connectionRequired &&
                                        <th>Total Watch Time</th>
                                    }
                                    {
                                        connectionRequired && 
                                        <th>Clicks</th>
                                    }
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    !initialLoading && collaborations.map((collaboration, i) => {
                                        return (
                                            <tr className="border-bottom-1 px-3" key={i}>
                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-2 border-right-dashed td-content">
                                                            <img src={collaboration.influencer?.social_account?.profile_picture_url ?? userProfilePicture} alt="Profile Pic" />
                                                            <span className="influencer-name">{collaboration.influencer.name}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                            <EyeIcon /> <span className="">{formatNumber(collaboration.views_count)}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                            <TargetIcon /> <span className="">{formatNumber(collaboration.interactions_count)}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                            <RupeeIcon2 /> <span className="">{collaboration.cpv}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                            <RupeeIcon2 /> <span className="">{collaboration.cpe}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                {
                                                    campaign?.mode !== 'offline' && connectionRequired &&
                                                    <td>
                                                        <div className="py-3">
                                                            {
                                                                collaboration.total_watch_time ? 
                                                                (<div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                                    <EyeIcon /> <span className="">{collaboration.total_watch_time ?? '-'}</span>
                                                                </div>) : '-'
                                                            }
                                                        </div>
                                                    </td>
                                                }
                                                { 
                                                    connectionRequired &&
                                                    <td>
                                                        <div className="py-3">
                                                            <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                            <WebIcon1 /> <span className="">{formatNumber(collaboration.clicks_count ?? 0)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                }
                                                <td>
                                                    <div className="py-3">
                                                        <button className="theme-btn btn-success-light mx-auto" onClick={() => setSelectedCollaboration(collaboration.influencer.uuid)}>
                                                            Post Metrics <i className='bx bx-right-arrow-alt ms-1' ></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                                {
                                    (loading || initialLoading) &&
                                    Array.from({ length: 10 }).map((_, i) => {
                                        return (
                                            <tr>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                {
                                                    connectionRequired &&
                                                    <>
                                                        <td><Skeleton /></td>
                                                        <td><Skeleton /></td>
                                                    </>
                                                }
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </table>
                        {
                            !(loading || initialLoading) && error &&
                            <div className="py-3 d-flex justify-content-center align-items-center">
                                <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                            </div>
                        }
                        {
                            !(loading || initialLoading) && collaborations.length === 0 &&
                            <div className="d-flex w-100 justify-content-center align-items-center my-5">
                                <div className="length-zero my-5">
                                    <h3>Currently there is no collaboration in your list</h3>
                                </div>
                            </div>
                        }
                    </InfiniteScroll>

                </div>
            </div>
            <PostPerformanceOffcanvas
                isOpen={!!selectedCollaboration}
                onClose={() => setSelectedCollaboration(null)}
                collaborationId={selectedCollaboration}
            />
        </React.Fragment>
    )
}

export default CollaborationsTable